// admin-client/src/pages/Stats.jsx
// =============================================================================
// Usage analytics — distinct from /cross-check (which is about discrepancy
// flags). This page answers: how much was the IPT used, by whom, on which
// device, and how that compares to what was booked.
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import MonthPicker, { prevMonthOfNow } from '../components/MonthPicker.jsx';

export default function Stats() {
  // Default to the previous month — current-month data is only meaningful
  // mid-cycle, and analytics workflows live in "look at last month" mode.
  const [month, setMonth] = useState(prevMonthOfNow());
  const [overview, setOverview] = useState(null);
  const [byUsage, setByUsage] = useState([]);
  const [byBooked, setByBooked] = useState([]);
  const [byNoShows, setByNoShows] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let abort = false;
    (async () => {
      setErr(null);
      try {
        const [o, u, b, n] = await Promise.all([
          api.stats.overview({ month }),
          api.stats.topPilots({ month, metric: 'usage', limit: 10 }),
          api.stats.topPilots({ month, metric: 'booked', limit: 10 }),
          api.stats.topPilots({ month, metric: 'no_shows', limit: 10 }),
        ]);
        if (abort) return;
        setOverview(o);
        setByUsage(u.rows);
        setByBooked(b.rows);
        setByNoShows(n.rows);
      } catch (e) {
        if (!abort) setErr(e.message);
      }
    })();
    return () => { abort = true; };
  }, [month]);

  // Derived per-spec metrics:
  //   Crew Utilisation = hours used / hours booked   (NOT vs available hours)
  //   Avg Booked / Day = hours_booked / days in month
  //   Avg Used   / Day = hours_used   / days in month
  const t = overview?.totals;
  const days = overview?.assumptions?.days_in_month || 0;
  const crewUtil = t && t.hours_booked > 0
    ? Math.round((t.total_hours_used / t.hours_booked) * 1000) / 10
    : null;
  const avgBookedPerDay = t && days > 0 ? Math.round((t.hours_booked / days) * 10) / 10 : 0;
  const avgUsedPerDay   = t && days > 0 ? Math.round((t.total_hours_used / days) * 10) / 10 : 0;
  // IPT utilisation = (crew sign-in hours + non-crew event hours) / total
  // available slot hours, where available = 24h × days × active devices.
  // Different from "Crew utilisation" — that one asks "of what was booked,
  // how much did crew actually use?"; this one asks "of the entire IPT
  // capacity, how much was occupied by *anything* (crew + Maintenance +
  // Training + leased operators)?".
  const ipts = t?.active_devices || 0;
  const availableSlotHours = ipts * 24 * (days || 0);
  const totalActuallyUsed  = (t?.total_hours_used || 0) + (t?.total_event_hours || 0);
  const iptUtil = availableSlotHours > 0
    ? Math.round((totalActuallyUsed / availableSlotHours) * 1000) / 10
    : null;

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-cathay-700">Statistics</h1>
          <p className="text-sm text-slate-600">
            Booked vs actual IPT usage for the chosen month. Discrepancy / flag analysis lives
            on the <Link to="/cross-check" className="text-cathay-700 hover:underline">Cross-check</Link> page.
          </p>
        </div>
        <MonthPicker value={month} onChange={setMonth} />
      </header>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

      {overview && (
        <>
          {/* Booked vs Used — paired cards so each "vs" sits in one box */}
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            <PairTile
              title="Sessions"
              left={{ label: 'Booked', value: t.booked_sessions ?? 0 }}
              right={{ label: 'Used',   value: t.sessions ?? 0 }}
              hint="consecutive-slot blocks vs completed iPad sign-ins"
            />
            <PairTile
              title="Hours"
              left={{ label: 'Booked', value: t.hours_booked ?? 0, suffix: 'h' }}
              right={{ label: 'Used',   value: t.total_hours_used ?? 0, suffix: 'h' }}
            />
            <PairTile
              title="Avg / day"
              left={{ label: 'Booked', value: avgBookedPerDay, suffix: 'h' }}
              right={{ label: 'Used',   value: avgUsedPerDay,   suffix: 'h' }}
              hint={`over ${days} days`}
            />
          </section>

          {/* Roll-up tiles: utilisation, crew counts, devices, auto-closed */}
          <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-sm">
            <Tile
              label="Crew utilisation"
              value={crewUtil != null ? `${crewUtil}%` : '—'}
              hint="hours used / hours booked"
              tone={crewUtil != null && crewUtil >= 70 ? 'green' : crewUtil != null && crewUtil < 30 ? 'amber' : null}
            />
            <Tile
              label="IPT utilisation"
              value={iptUtil != null ? `${iptUtil}%` : '—'}
              hint={`${totalActuallyUsed} h of ${availableSlotHours} h (crew + events / 24h × ${days}d × ${ipts} IPT)`}
              tone={iptUtil != null && iptUtil >= 60 ? 'green' : iptUtil != null && iptUtil < 25 ? 'amber' : null}
            />
            <Tile
              label="Individual crew"
              value={t.unique_booked_pilots ?? 0}
              hint="distinct CathayIDs booked this month"
            />
            <Tile label="Active devices" value={t.active_devices ?? 0} />
            <Tile label="Auto-closed sessions" value={t.auto_closed_sessions ?? 0} tone="amber" />
          </section>

          {/* Per-device */}
          <section className="bg-white rounded shadow-sm border p-4">
            <h2 className="font-medium text-slate-800 mb-2">Per-device booked vs used</h2>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 text-xs">
                  <th className="py-1">Device</th>
                  <th className="py-1">Location</th>
                  <th className="py-1 text-right">Sessions booked</th>
                  <th className="py-1 text-right">Sessions used</th>
                  <th className="py-1 text-right">Hours booked</th>
                  <th className="py-1 text-right">Hours used</th>
                  <th className="py-1 text-right">Crew util.</th>
                  <th className="py-1 text-right">IPT util.</th>
                </tr>
              </thead>
              <tbody>
                {overview.per_device.map((d) => {
                  const util = d.hours_booked > 0
                    ? Math.round((d.hours_used / d.hours_booked) * 1000) / 10
                    : null;
                  const devEventHours = Object.values(d.events_by_type || {})
                    .reduce((a, n) => a + n, 0);
                  const devAvail = 24 * (days || 0); // per single device
                  const devUsedTotal = (d.hours_used || 0) + devEventHours;
                  const iptUtilDev = devAvail > 0
                    ? Math.round((devUsedTotal / devAvail) * 1000) / 10
                    : null;
                  return (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="py-1.5 font-medium">{d.name}</td>
                      <td className="py-1.5 text-slate-600">{d.location || '—'}</td>
                      <td className="py-1.5 text-right">{d.booked_sessions ?? 0}</td>
                      <td className="py-1.5 text-right">{d.sessions}</td>
                      <td className="py-1.5 text-right">{d.hours_booked}</td>
                      <td className="py-1.5 text-right">{d.hours_used}</td>
                      <td className="py-1.5 text-right">
                        {util != null ? (
                          <span className={
                            'inline-block px-2 py-0.5 rounded text-xs ' +
                            (util > 70
                              ? 'bg-green-100 text-green-800'
                              : util > 30
                                ? 'bg-cathay-100 text-cathay-800'
                                : 'bg-slate-100 text-slate-700')
                          }>{util}%</span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td
                        className="py-1.5 text-right"
                        title={`${devUsedTotal}h actually used (${d.hours_used || 0}h crew + ${devEventHours}h events) of ${devAvail}h available (24h × ${days}d)`}
                      >
                        {iptUtilDev != null ? (
                          <span className={
                            'inline-block px-2 py-0.5 rounded text-xs ' +
                            (iptUtilDev >= 60
                              ? 'bg-green-100 text-green-800'
                              : iptUtilDev >= 25
                                ? 'bg-cathay-100 text-cathay-800'
                                : 'bg-slate-100 text-slate-700')
                          }>{iptUtilDev}%</span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
                {overview.per_device.length === 0 && (
                  <tr><td colSpan={8} className="py-3 text-center text-slate-500">No active devices.</td></tr>
                )}
              </tbody>
            </table>
            <div className="text-xs text-slate-500 mt-2 space-y-0.5">
              <div>
                <strong>Crew util.</strong> = hours used ÷ hours booked. 100% means every booked hour was used; over 100% means more time logged than was booked (drop-ins).
              </div>
              <div>
                <strong>IPT util.</strong> = (hours used by crew + hours of non-crew events) ÷ 24h × days. Tracks total IPT occupancy regardless of booking source.
              </div>
            </div>
          </section>
        </>
      )}

      {/* Top pilots — three lists */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TopList title="Most time in IPT" rows={byUsage} valueCol="minutes_used" formatter={(m) => `${Math.round((m / 60) * 10) / 10} h`} />
        <TopList title="Most booked" rows={byBooked} valueCol="hours_booked" formatter={(h) => `${h} h`} />
        <TopList title="Most no-shows (this month)" rows={byNoShows} valueCol="no_show_count" formatter={(c) => `${c}×`} tone="red" />
      </section>

      {/* Usage-type distribution chart */}
      {overview && <UsageDistribution overview={overview} />}
    </div>
  );
}

/* =============================================================================
   UsageDistribution — vertical bar chart of hours by usage type.
   Aggregates Crew bookings + every ipt_events.event_type seen in the month.
   Optional device filter narrows the view to a single IPT.
   Pure-SVG, no chart library — small, themable, accessible.
   ============================================================================= */
function UsageDistribution({ overview }) {
  // 'all' or a device id
  const [scope, setScope] = useState('all');

  // Build a row per usage-type with hours for the chosen scope.
  // Crew is always first; event types follow in descending-hours order.
  const rows = (() => {
    if (scope === 'all') {
      const crewHours = overview.per_device.reduce((a, d) => a + (d.hours_booked || 0), 0);
      const evByType = { ...(overview.totals.event_hours_by_type || {}) };
      const evRows = Object.entries(evByType).map(([type, hours]) => ({ type, hours }));
      evRows.sort((a, b) => b.hours - a.hours);
      return [{ type: 'Crew', hours: crewHours }, ...evRows];
    }
    const dev = overview.per_device.find((d) => d.id === scope);
    if (!dev) return [];
    const evRows = Object.entries(dev.events_by_type || {})
      .map(([type, hours]) => ({ type, hours }));
    evRows.sort((a, b) => b.hours - a.hours);
    return [{ type: 'Crew', hours: dev.hours_booked || 0 }, ...evRows];
  })();

  // Colour palette — Crew gets the booked-pill colour, the rest cycle.
  // (We deliberately don't reuse the CrewLink legend colours: they're
  // legible inside Excel but garish for a Cathay-themed dashboard.)
  const COLORS = {
    Crew:               '#0e7490', // cyan-700  — booked crew accent
    'IPT Maintenance':  '#dc2626', // red-600
    'Fleet TRNG':       '#16a34a', // green-600
    'Contract TRNG':    '#2563eb', // blue-600
    STM:                '#65a30d', // lime-600
    TTS:                '#ca8a04', // yellow-600
    AHK:                '#1e3a8a', // indigo-900
    HKE:                '#a21caf', // fuchsia-700
  };
  const fallback = '#475569'; // slate-600
  const colorOf = (t) => COLORS[t] || fallback;

  const totalHours = rows.reduce((a, r) => a + r.hours, 0);
  const max = Math.max(1, ...rows.map((r) => r.hours));

  // Chart sizing
  const PAD_X = 56;
  const PAD_TOP = 16;
  const PAD_BOTTOM = 76; // room for rotated labels
  const BAR_W = 56;
  const BAR_GAP = 28;
  const innerWidth = rows.length * (BAR_W + BAR_GAP) + BAR_GAP;
  const W = Math.max(560, PAD_X * 2 + innerWidth);
  const H = 320;

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-medium text-slate-800">Usage-type distribution</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Hours by usage type {scope === 'all'
              ? 'across every IPT'
              : `for ${overview.per_device.find((d) => d.id === scope)?.name || 'this IPT'}`}.
            {totalHours > 0 ? ` Total ${Math.round(totalHours * 10) / 10}h.` : ''}
          </p>
        </div>
        <label className="text-sm">
          <div className="text-xs text-slate-500">Scope</div>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm min-w-[200px]"
          >
            <option value="all">All IPTs combined</option>
            {overview.per_device.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </label>
      </header>

      {rows.length === 0 || totalHours === 0 ? (
        <div className="py-8 text-center text-slate-500 text-sm">No usage data for this scope.</div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            className="w-full"
            style={{ maxWidth: W }}
          >
            {/* Y axis: 4 horizontal gridlines + labels */}
            {Array.from({ length: 5 }, (_, i) => {
              const v = (max / 4) * (4 - i);
              const y = PAD_TOP + ((H - PAD_TOP - PAD_BOTTOM) * i) / 4;
              return (
                <g key={`y-${i}`}>
                  <line
                    x1={PAD_X} x2={W - PAD_X / 2}
                    y1={y} y2={y}
                    stroke="#e2e8f0" strokeDasharray={i === 4 ? '' : '2 4'}
                  />
                  <text
                    x={PAD_X - 8} y={y + 4}
                    textAnchor="end" fontSize="10" fill="#64748b"
                  >
                    {Math.round(v)}h
                  </text>
                </g>
              );
            })}

            {/* Bars + labels */}
            {rows.map((r, i) => {
              const x = PAD_X + BAR_GAP + i * (BAR_W + BAR_GAP);
              const heightPx = ((H - PAD_TOP - PAD_BOTTOM) * r.hours) / max;
              const y = (H - PAD_BOTTOM) - heightPx;
              const pct = totalHours > 0 ? Math.round((r.hours / totalHours) * 1000) / 10 : 0;
              return (
                <g key={r.type}>
                  <title>{`${r.type}: ${r.hours}h (${pct}%)`}</title>
                  <rect
                    x={x} y={y} width={BAR_W} height={heightPx}
                    fill={colorOf(r.type)} rx="3"
                  />
                  {/* Hour value above bar */}
                  {r.hours > 0 && (
                    <text
                      x={x + BAR_W / 2}
                      y={y - 6}
                      textAnchor="middle"
                      fontSize="11" fill="#0f172a" fontWeight="600"
                    >
                      {r.hours}h
                    </text>
                  )}
                  {/* Type label, rotated */}
                  <text
                    x={x + BAR_W / 2}
                    y={H - PAD_BOTTOM + 16}
                    textAnchor="end"
                    fontSize="11" fill="#334155"
                    transform={`rotate(-35, ${x + BAR_W / 2}, ${H - PAD_BOTTOM + 16})`}
                  >
                    {r.type}
                  </text>
                  {/* Percent label, below type */}
                  <text
                    x={x + BAR_W / 2}
                    y={H - PAD_BOTTOM + 30}
                    textAnchor="end"
                    fontSize="10" fill="#64748b"
                    transform={`rotate(-35, ${x + BAR_W / 2}, ${H - PAD_BOTTOM + 30})`}
                  >
                    {pct}%
                  </text>
                </g>
              );
            })}

            {/* Baseline */}
            <line
              x1={PAD_X} x2={W - PAD_X / 2}
              y1={H - PAD_BOTTOM} y2={H - PAD_BOTTOM}
              stroke="#cbd5e1"
            />
          </svg>
        </div>
      )}
    </section>
  );
}

/**
 * PairTile — two related metrics inside one card. Used on the Stats page
 * to keep "Booked vs Used" pairs together visually.
 *   <PairTile
 *     title="Hours"
 *     left={{ label: 'Booked', value: 240, suffix: 'h' }}
 *     right={{ label: 'Used',   value: 217, suffix: 'h' }}
 *     hint="optional bottom caption"
 *   />
 */
function PairTile({ title, left, right, hint }) {
  return (
    <div className="rounded p-3 border bg-white border-slate-200">
      <div className="text-xs text-slate-500">{title}</div>
      <div className="mt-1 grid grid-cols-2 gap-2">
        <PairSide {...left} accent="cathay" />
        <PairSide {...right} accent="green" />
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-2">{hint}</div>}
    </div>
  );
}
function PairSide({ label, value, suffix, accent }) {
  const accents = {
    cathay: 'text-cathay-700',
    green:  'text-green-700',
  };
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={'text-2xl font-semibold leading-tight ' + (accents[accent] || 'text-slate-800')}>
        {value}
        {suffix && <span className="text-base font-normal text-slate-500 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function Tile({ label, value, hint, tone }) {
  const tones = {
    amber: 'bg-amber-50 text-amber-800',
    red: 'bg-red-50 text-red-700',
    green: 'bg-green-50 text-green-800',
  };
  return (
    <div className={'rounded p-3 border ' + (tone ? `${tones[tone]} border-transparent` : 'bg-white border-slate-200')}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-cathay-700 mt-0.5">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function TopList({ title, rows, valueCol, formatter, tone }) {
  return (
    <div className="bg-white rounded shadow-sm border p-4">
      <h3 className="font-medium text-slate-800 mb-2">{title}</h3>
      <table className="min-w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.cathay_id} className="border-t border-slate-100">
              <td className="py-1.5 text-slate-400 w-8">{i + 1}.</td>
              <td className="py-1.5 font-mono">{r.cathay_id}</td>
              <td className="py-1.5 text-slate-600 truncate">{r.display_name || ''}</td>
              <td className={'py-1.5 text-right font-medium ' + (tone === 'red' ? 'text-red-700' : 'text-slate-800')}>
                {formatter(r[valueCol])}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="py-3 text-center text-slate-500">No data.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
